export interface AvatarPalette {
  bg: string
  fg: string
  ring?: string
}

const DEFAULT_AVATAR_PALETTE = { bg: '#1a5a44', ring: '#349e7c' }

// Identity palette — hashed per user for avatar backgrounds. This is a data-viz
// identity set, deliberately broader than the two-source brand palette (ADR 06);
// the retired Lake teals were swapped for the functional Info/Slate.
const AVATAR_PALETTE: { bg: string; ring: string }[] = [
  DEFAULT_AVATAR_PALETTE,
  { bg: '#134836', ring: '#d5e4dd' },
  { bg: '#355c82', ring: '#7ba3ce' },
  { bg: '#22303f', ring: '#5582b0' },
  { bg: '#a8421a', ring: '#f0a679' },
  { bg: '#c9531a', ring: '#f8e3d4' },
  { bg: '#1f3a31', ring: '#349e7c' },
  { bg: '#3a2519', ring: '#ff8343' },
]

const LIGHT_FG = '#ffffff'
const DARK_FG = '#000000'

export function avatarPalette(seed: string): AvatarPalette {
  const choice = AVATAR_PALETTE[hashUtf8(seed) % AVATAR_PALETTE.length] ?? DEFAULT_AVATAR_PALETTE
  const fg = contrastRatio(choice.bg, LIGHT_FG) >= 4.5 ? LIGHT_FG : DARK_FG
  return { bg: choice.bg, fg, ring: choice.ring }
}

/**
 * Inline style for the "ring stop → body stop" avatar treatment described in
 * `wiki/decisions/06-design-system.md` §Avatar palette. Single source for the
 * topbar avatar (`AppShell`), the `MePage` profile header, and the per-user
 * dots inside `PresenceAvatarStack`. Doc-blessed exception to the general
 * "no `bg-gradient-*`" rule because the gradient is built from two palette
 * stops, not a free-form colour ramp.
 */
export function avatarStyle(palette: AvatarPalette): {
  background: string
  borderColor: string
  boxShadow: string
  color: string
} {
  const ring = palette.ring ?? palette.bg
  return {
    background: `linear-gradient(145deg, ${ring} 0%, ${palette.bg} 58%, ${palette.bg} 100%)`,
    borderColor: ring,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -8px 14px rgba(14,20,18,0.16)',
    color: palette.fg,
  }
}

export function avatarInitial(name: string, locale: 'zh' | 'en' = 'zh'): string {
  const trimmed = name.trim()
  const fallback = locale === 'zh' ? '寻' : 'X'
  if (!trimmed) return fallback
  const segmenter = getSegmenter()
  const first = segmenter
    ? Array.from(segmenter.segment(trimmed))[0]?.segment
    : Array.from(trimmed)[0]
  if (!first || isPictographic(first)) return fallback
  return first.toLocaleUpperCase(locale)
}

export function avatarContrastRatio(a: string, b: string): number {
  return contrastRatio(a, b)
}

function hashUtf8(input: string): number {
  const bytes = new TextEncoder().encode(input || 'XingRoute')
  let hash = 0x811c9dc5
  for (const byte of bytes) {
    hash ^= byte
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

function getSegmenter(): Intl.Segmenter | null {
  if (!('Segmenter' in Intl)) return null
  return new Intl.Segmenter(undefined, { granularity: 'grapheme' })
}

function isPictographic(value: string): boolean {
  return /\p{Extended_Pictographic}/u.test(value)
}

function contrastRatio(a: string, b: string): number {
  const lighter = Math.max(relativeLuminance(a), relativeLuminance(b))
  const darker = Math.min(relativeLuminance(a), relativeLuminance(b))
  return (lighter + 0.05) / (darker + 0.05)
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((channel) => {
    const scaled = channel / 255
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0)
}

function parseHex(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '')
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return [0, 0, 0]
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ]
}
